import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Mail, Send, Trash2, AlertCircle, RefreshCw, Check, Clock, Lock, Shield } from "lucide-react";
import { useBrutalConfirm } from "@/components/BrutalConfirm";

/**
 * League admin → Invite by Email card.
 *
 * Caps total invites at min(20, team_count). The admin's own participant row
 * counts toward the cap, so a 20-team league with the admin playing leaves
 * 19 slots for invites. When the admin opts out of playing, all 20 are free.
 *
 * iter35f — Houses the Strict Invites section as a sub-section so the two
 * related concepts (admin-invite flow + the lockdown that enforces it) live
 * together in the AdminPanel UI.
 * iter87.97 — Strict Invites is now the platform-wide default and cannot be
 * turned off. The section stays as an operator reassurance ("Always on").
 */
export default function InviteByEmailCard({ leagueId, league, isLocked, onChanged }) {
    // iter87.135 — `window.confirm` silently no-ops on mobile inside a
    // Radix Dialog (operator report: "Delete is not working here either.
    // Email pending."). Same fix as iter87.134's crewmate-delete flow.
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();
    const [meta, setMeta] = useState(null);
    const [invites, setInvites] = useState([]);
    const [text, setText] = useState("");
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const data = await api.listInviteEmails(leagueId);
            setMeta(data);
            setInvites(data.invites || []);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to load invites");
        }
        setLoading(false);
    }, [leagueId]);

    useEffect(() => { load(); }, [load]);

    const parsedEmails = text
        .split(/[\s,;\n]+/)
        .map(e => e.trim())
        .filter(Boolean);
    const remaining = meta?.remaining ?? 0;
    const wouldExceed = parsedEmails.length > remaining;

    const handleSend = async () => {
        if (parsedEmails.length === 0) {
            toast.error("Add at least one email address");
            return;
        }
        setSending(true);
        try {
            const r = await api.sendInviteEmails(leagueId, parsedEmails);
            const sentCount = (r.sent || []).length;
            const skipCount = (r.skipped || []).length;
            const errCount = (r.errors || []).length;
            if (sentCount > 0) {
                toast.success(`${sentCount} invite${sentCount === 1 ? "" : "s"} sent`);
            }
            if (skipCount > 0) {
                const reasons = (r.skipped || []).reduce((acc, s) => {
                    acc[s.reason] = (acc[s.reason] || 0) + 1;
                    return acc;
                }, {});
                const summary = Object.entries(reasons)
                    .map(([k, v]) => `${v} ${k.replace(/_/g, " ")}`)
                    .join(", ");
                toast(`Skipped: ${summary}`, { icon: "⚠️" });
            }
            if (errCount > 0) {
                toast.error(`${errCount} delivery error${errCount === 1 ? "" : "s"} — invites still recorded; resend later`);
            }
            setText("");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Send failed");
        }
        setSending(false);
    };

    const handleRevoke = async (inviteId, email) => {
        const ok = await brutalConfirm({
            title: `Revoke invite to ${email}?`,
            body: "This frees up a slot in the Rivalry. The invitee's magic link and personal code will no longer work.",
            confirmLabel: "Revoke invite",
            cancelLabel: "Cancel",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.revokeInviteEmail(leagueId, inviteId);
            toast.success("Invite revoked");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Revoke failed");
        }
    };

    // iter72 — Resend the original join-invite email. Same Resend template
    // used for forgot-username, modified for "join your Rivalry" copy.
    const [resendingId, setResendingId] = useState(null);
    const handleResend = async (inviteId, email) => {
        setResendingId(inviteId);
        try {
            await api.resendInviteEmail(leagueId, inviteId);
            toast.success(`Re-sent invite to ${email}`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Resend failed");
        }
        setResendingId(null);
    };

    return (
        <div className="brutal-card p-5 space-y-4" data-testid="invite-by-email-card">
            <div className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-cyber" />
                <h2 className="font-display font-extrabold text-xl uppercase">Invite by email</h2>
            </div>
            <p className="text-xs text-zinc-400 leading-relaxed">
                Send up to <span className="text-cyber font-bold">{meta?.max_total ?? 20}</span> email invitations per league
                ({meta?.max_total === 20 ? "or 19 if you're also playing" : "league cap depends on your tournament's team count"}).
                Recipients get the invite code + a one-click join link, and a reminder that usernames are <strong>case-sensitive</strong>.
            </p>

            {/* Status pill */}
            {meta && (
                <div className="grid grid-cols-3 gap-2" data-testid="invite-cap-stats">
                    <div className="brutal-card-light p-3 text-center">
                        <p className="text-[10px] uppercase font-bold text-zinc-500">Crewmates</p>
                        <p className="font-display font-extrabold text-lg">{meta.participants}</p>
                    </div>
                    <div className="brutal-card-light p-3 text-center">
                        <p className="text-[10px] uppercase font-bold text-zinc-500">Pending</p>
                        <p className="font-display font-extrabold text-lg">{meta.pending_invites}</p>
                    </div>
                    <div className="brutal-card-light p-3 text-center border-cyber/40">
                        <p className="text-[10px] uppercase font-bold text-zinc-500">Slots left</p>
                        <p className="font-display font-extrabold text-lg text-cyber">{meta.remaining}</p>
                    </div>
                </div>
            )}

            {/* iter35f — Strict Invites sub-section.
                iter87.97 — Toggle removed; strict invites is the ONLY mode
                across the platform now. Section stays as a visible reassurance
                to operators that only invited emails can join. */}
            <div className="brutal-card-light p-3 space-y-2 border-cyber/50" data-testid="strict-invites-subsection">
                <div className="flex items-center gap-2 min-w-0">
                    <Shield className="w-4 h-4 text-cyber" />
                    <p className="text-[11px] uppercase tracking-widest font-bold">Strict invites</p>
                    <span className="pill border border-cyber/60 text-cyber text-[9px] py-0 px-1.5 uppercase font-bold">Always on</span>
                </div>
                <p className="text-[11px] text-zinc-400 leading-relaxed">
                    Only players you invite by email (or add via Crewmates tab) can sign in. They use their personal code from the invite email — case-insensitive username matching prevents typo-duplicates.
                </p>
            </div>

            {/* Compose */}
            <div>
                <label className="block text-[10px] uppercase font-bold text-zinc-500 mb-1">
                    Email addresses <span className="text-zinc-600 normal-case font-medium tracking-normal">— comma, space, or newline separated</span>
                </label>
                <textarea
                    data-testid="invite-emails-textarea"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder="alice@example.com, bob@example.com&#10;carol@example.com"
                    className="brutal-input w-full text-sm h-24 resize-y font-mono"
                    disabled={isLocked || remaining === 0}
                    spellCheck={false}
                />
                <div className="flex items-center justify-between flex-wrap gap-2 mt-2">
                    <p className="text-[11px] text-zinc-500" data-testid="invite-parsed-count">
                        {parsedEmails.length} email{parsedEmails.length === 1 ? "" : "s"} parsed
                        {wouldExceed && (
                            <span className="text-blaze font-bold ml-2">
                                · {parsedEmails.length - remaining} over the cap
                            </span>
                        )}
                    </p>
                    <button
                        data-testid="send-invite-emails-btn"
                        onClick={handleSend}
                        disabled={sending || isLocked || parsedEmails.length === 0 || remaining === 0}
                        className="brutal-btn-primary text-sm py-2 disabled:opacity-40"
                    >
                        {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                        Send invites
                    </button>
                </div>
            </div>

            {isLocked && (
                <p className="text-[11px] text-zinc-500 flex items-center gap-1.5" data-testid="invite-locked-note">
                    <Lock className="w-3 h-3" /> Rivalry is closed or archived — new invites disabled.
                </p>
            )}
            {!isLocked && remaining === 0 && (
                <p className="text-[11px] text-blaze flex items-center gap-1.5" data-testid="invite-cap-reached-note">
                    <AlertCircle className="w-3 h-3" /> Cap reached. Revoke a pending invite to free a slot.
                </p>
            )}

            {/* History */}
            {invites.length > 0 && (
                <div>
                    <p className="text-[10px] uppercase font-bold text-zinc-500 mb-2">Invite history</p>
                    <ul className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="invite-history-list">
                        {invites.map(inv => (
                            <li
                                key={inv.id}
                                data-testid={`invite-row-${inv.id}`}
                                className={`brutal-card-light p-2.5 flex items-center justify-between gap-2 text-xs ${inv.joined ? "border-cyber/40" : ""}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="font-mono truncate">{inv.email}</p>
                                    <p className="text-[10px] text-zinc-500 mt-0.5">
                                        Sent {new Date(inv.sent_at).toLocaleString()}
                                        {/* iter72 — show resend lineage if any */}
                                        {inv.resent_count > 0 && (
                                            <span
                                                data-testid={`invite-resent-tag-${inv.id}`}
                                                className="ml-2 text-volt"
                                                title={`Last re-sent ${new Date(inv.resent_at).toLocaleString()}`}
                                            >
                                                · re-sent {inv.resent_count}×
                                            </span>
                                        )}
                                    </p>
                                </div>
                                {inv.joined ? (
                                    <span className="pill bg-cyber text-black text-[10px] font-bold flex items-center gap-1">
                                        <Check className="w-3 h-3" /> JOINED
                                    </span>
                                ) : (
                                    <>
                                        <span className="pill bg-ink border-[#333] text-[10px] flex items-center gap-1">
                                            <Clock className="w-3 h-3" /> PENDING
                                        </span>
                                        <button
                                            data-testid={`resend-invite-${inv.id}-btn`}
                                            onClick={() => handleResend(inv.id, inv.email)}
                                            disabled={resendingId === inv.id}
                                            className="brutal-btn-ghost text-[10px] py-1 px-2 disabled:opacity-50"
                                            title="Re-send the join invite email"
                                        >
                                            {resendingId === inv.id
                                                ? <RefreshCw className="w-3 h-3 text-cyber animate-spin" />
                                                : <Send className="w-3 h-3 text-cyber" />}
                                        </button>
                                        <button
                                            data-testid={`revoke-invite-${inv.id}-btn`}
                                            onClick={() => handleRevoke(inv.id, inv.email)}
                                            className="brutal-btn-ghost text-[10px] py-1 px-2"
                                            title="Revoke invite (frees a slot)"
                                        >
                                            <Trash2 className="w-3 h-3 text-heat" />
                                        </button>
                                    </>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {invites.length === 0 && !loading && (
                <p className="text-xs text-zinc-500 italic" data-testid="invite-history-empty">
                    No invites sent yet.
                </p>
            )}
            {/* iter87.135 — Mount the BrutalConfirm host so the revoke
                prompt renders on mobile inside the Admin Takeover. */}
            {confirmHost}
        </div>
    );
}
